import React, { useMemo } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Trophy, Target } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import { useLeagues, useStandings, useScorers } from '../../hooks/useSports'
import { cn } from '../../lib/utils'

// Header cells. Compact codes (P / W / D / L / GD / Pts) since the
// table is narrow on mobile. Tooltip-via-title would be nice but
// touch-only doesn't render tooltips well — the codes are
// universally recognised in football.
const COLS = [
  { key: 'playedGames', label: 'P' },
  { key: 'won', label: 'W' },
  { key: 'draw', label: 'D' },
  { key: 'lost', label: 'L' },
  { key: 'goalDifference', label: 'GD' },
  { key: 'points', label: 'Pts', emphasise: true },
]

export function SportsStandingsPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { leagueId } = useParams()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const isArabic = locale === 'ar'

  const leaguesHook = useLeagues()
  const league = useMemo(
    () => leaguesHook.leagues.find((l) => l.id === leagueId) || null,
    [leaguesHook.leagues, leagueId],
  )
  const competitionCode = league?.provider_code || null
  const { data, loading, error } = useStandings(competitionCode)
  const scorersHook = useScorers(competitionCode)

  // Football-data returns multiple standings tables (TOTAL / HOME /
  // AWAY) for normal leagues and one-per-group for group stages of
  // tournaments. Render the TOTAL table for leagues and all group
  // tables for tournaments.
  const tables = useMemo(() => {
    if (!Array.isArray(data?.standings)) return []
    const tournament = data.standings.length > 1 && data.standings[0]?.group
    if (tournament) {
      return data.standings.filter((s) => s.type === 'TOTAL' || !s.type)
    }
    return data.standings.filter((s) => s.type === 'TOTAL')
  }, [data])

  const goBack = () => navigate(-1)
  const leagueLabel = league
    ? (isArabic && league.name_ar ? league.name_ar : league.name)
    : ''
  const seasonLabel = data?.season
    ? `${(data.season.startDate || '').slice(0, 4)}-${(data.season.endDate || '').slice(2, 4)}`
    : ''

  return (
    <div className="space-y-3 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={goBack}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-sm font-semibold text-muted-foreground flex items-center gap-1.5" dir="auto">
            <Trophy className="w-4 h-4 text-app-primary-500" />
            {leagueLabel || t('sports.standings.title')}
          </h1>
          {seasonLabel && (
            <p className="text-[11px] text-muted-foreground font-mono" dir="ltr">{seasonLabel}</p>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="p-3 text-xs text-red-600" dir="auto">{error}</CardContent>
        </Card>
      ) : tables.length === 0 ? (
        <EmptyState icon={Trophy} title={t('sports.standings.empty')} />
      ) : (
        <div className="space-y-3">
          {/* Top scorers — section above the table so they're visible
              on first paint without scrolling past 20 rows. Tournament
              standings (group stage) might lack scorers; hide the
              section quietly when so. */}
          {scorersHook.data?.scorers?.length > 0 && (
            <Card>
              <CardContent className="p-3 sm:p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2 px-1 inline-flex items-center gap-1.5" dir="auto">
                  <Target className="w-3.5 h-3.5 text-app-primary-500" />
                  {t('sports.standings.topScorers')}
                </p>
                <ul className="divide-y divide-border/30">
                  {scorersHook.data.scorers.map((s, i) => (
                    <li key={`${s.player?.id}-${i}`} className="flex items-center gap-2 py-1.5 text-xs">
                      <span className="w-5 text-center text-muted-foreground font-mono tabular-nums" dir="ltr">
                        {i + 1}
                      </span>
                      <RouterLink
                        to={`/sports/teams/${s.team?.id}`}
                        className="flex items-center gap-1.5 flex-1 min-w-0 hover:text-app-primary-500 transition-colors"
                      >
                        {s.team?.crest && (
                          <img src={s.team.crest} alt="" loading="lazy" className="w-3.5 h-3.5 object-contain flex-shrink-0" />
                        )}
                        <span className="text-foreground truncate" dir="auto">
                          {s.player?.name}
                        </span>
                      </RouterLink>
                      <span className="text-[10px] text-muted-foreground truncate hidden sm:inline" dir="auto">
                        {s.team?.shortName || s.team?.tla}
                      </span>
                      <span
                        className="inline-flex items-center gap-1 font-mono tabular-nums text-foreground font-semibold"
                        dir="ltr"
                      >
                        {s.goals ?? 0}
                        {s.assists != null && (
                          <span className="text-[10px] text-muted-foreground">
                            (+{s.assists})
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {tables.map((s, idx) => (
            <Card key={`${s.group || 'table'}-${idx}`}>
              <CardContent className="p-3 sm:p-4">
                {s.group && (
                  <p className="text-xs font-semibold text-foreground mb-2 uppercase tracking-wide" dir="auto">
                    {s.group.replace('_', ' ')}
                  </p>
                )}
                <div className="overflow-x-auto -mx-1">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-muted-foreground border-b border-border/60">
                        <th className="text-center w-7 py-1.5 font-medium" dir="ltr">#</th>
                        <th className="text-start py-1.5 ps-1 font-medium" dir="auto">
                          {t('sports.standings.team')}
                        </th>
                        {COLS.map((c) => (
                          <th
                            key={c.key}
                            className={cn('text-center w-8 py-1.5 font-medium font-mono', c.emphasise && 'text-foreground')}
                            dir="ltr"
                          >
                            {c.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(s.table || []).map((row) => (
                        <tr
                          key={row.team?.id || row.position}
                          className="border-b border-border/30 last:border-b-0"
                        >
                          <td className="text-center text-muted-foreground font-mono py-1.5" dir="ltr">
                            {row.position}
                          </td>
                          <td className="ps-1 py-1.5">
                            <RouterLink
                              to={`/sports/teams/${row.team?.id}`}
                              className="flex items-center gap-1.5 min-w-0 hover:text-app-primary-500 transition-colors"
                            >
                              {row.team?.crest && (
                                <img
                                  src={row.team.crest}
                                  alt=""
                                  loading="lazy"
                                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                                  className="w-4 h-4 object-contain flex-shrink-0"
                                />
                              )}
                              <span
                                className="truncate text-foreground"
                                dir="auto"
                                title={row.team?.name}
                              >
                                {row.team?.shortName || row.team?.tla || row.team?.name}
                              </span>
                            </RouterLink>
                          </td>
                          {COLS.map((c) => (
                            <td
                              key={c.key}
                              className={cn(
                                'text-center font-mono py-1.5 tabular-nums',
                                c.emphasise ? 'text-foreground font-semibold' : 'text-muted-foreground',
                              )}
                              dir="ltr"
                            >
                              {row[c.key] ?? '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
